
import React from 'react';
import { Box, Typography, Tooltip, Divider } from '@mui/material';
import {
    Edit as EditIcon,
    NotificationsActive as NotificationIcon,
    OpenInFull as OpenIcon,
    LocationOn as LocationIcon,
    PlayArrow as PlayIcon,
    CheckCircle as CheckCircleIcon,
    FiberManualRecord as FiberManualRecordIcon
} from '@mui/icons-material';
import moment from 'moment';

const TimelineNode = ({ icon, borderColor, bgColor, pulse, tooltipTitle, extra, label, time, passed, current }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '20%' }}>

        <Tooltip title={tooltipTitle} arrow>
            <Box sx={{
                width: 32, height: 32, border: `2px solid ${borderColor}`, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: bgColor || 'transparent',
                animation: pulse ? 'pulseZoom 2s infinite ease-in-out' : 'none',
                '@keyframes pulseZoom': {
                    '0%': { backgroundColor: 'transparent', transform: 'scale(1)' },
                    '50%': { backgroundColor: current ? '#ffcdd2' : 'transparent', transform: 'scale(1.2)' },
                    '100%': { backgroundColor: 'transparent', transform: 'scale(1)' },
                },
                cursor: 'help',
                zIndex: 2,
                mb: 1
            }}>
                {React.cloneElement(icon, { sx: { fontSize: 16, color: borderColor } })}
            </Box>
        </Tooltip>
        <Typography variant="caption" sx={{ fontWeight: 'bold', textAlign: 'center', color: borderColor, fontSize: '0.65rem', lineHeight: 1.1 }}>
            {label}
        </Typography>
        <Typography variant="caption" sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.6rem' }}>
            {time || '--:-- --/--'}
        </Typography>
    </Box>
);

const Connector = ({ active, pulse }) => (
    <Box sx={{
        flexGrow: 1, height: 3, mx: -1, mt: 2,
        background: active ? '#2e7d32' : 'rgba(0,0,0,0.1)',
        zIndex: 1,
        position: 'relative',
        top: -24
    }} />
);

const TimelineRonda = ({ ronda, darkMode, themeHelper }) => {
    const currentStatus = Number(ronda?.status_ronda_motorizada); // 0: Aberta, 1: Concluída
    
    // Ensure logs and procedimentos are arrays
    const rondaLogs = Array.isArray(ronda?.logs) ? ronda.logs : [];
    const rondaProcedimentos = Array.isArray(ronda?.procedimentos) ? ronda.procedimentos : [];

    // Find logs for each stage
    const findLogTime = (type) => {
        const log = rondaLogs.find(l => l.tipo === type);
        if (!log) return null;
        return moment(log.data_hora).format('HH:mm DD/MM');
    };

    const stages = [
        { id: 1, label: 'Criada', type: 'Ronda Motorizada Criada', icon: <EditIcon /> },
        { id: 2, label: 'Notificado', type: 'Notificação Enviada', icon: <NotificationIcon /> },
        { id: 3, label: 'Aberta', type: 'Ronda Motorizada Aberta', icon: <OpenIcon /> },
        { id: 4, label: 'No Local', type: 'Chegada ao Local', icon: <LocationIcon /> },
        { id: 5, label: 'Finalizada', type: 'Ronda Finalizada', icon: <CheckCircleIcon /> }
    ];

    // Determine the furthest stage reached
    let furthestStage = 0;
    if (findLogTime('Ronda Motorizada Criada')) furthestStage = 1;
    if (findLogTime('Notificação Enviada') || findLogTime('Notificação Recebida')) furthestStage = 2;
    if (findLogTime('Ronda Motorizada Aberta') || ronda?.rondante_abriu_ronda === 1) furthestStage = 3;
    if (findLogTime('Chegada ao Local')) furthestStage = 4;
    if (currentStatus === 10) furthestStage = 5;


    return (
        <Box sx={{ my: 4, width: '100%' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, color: darkMode ? '#ccc' : '#666', textTransform: 'uppercase', mb: 3 }}>
                Linha do Tempo Operacional
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', position: 'relative', px: 2 }}>
                {stages.map((stage, idx) => {
                    const isPassed = furthestStage > stage.id || (stage.id === 5 && currentStatus === 10);
                    const isCurrent = furthestStage === stage.id && stage.id !== 5;

                    const isFuture = furthestStage < stage.id;
                    
                    const color = isPassed ? '#2e7d32' : isCurrent ? '#f44336' : (darkMode ? '#555' : '#ccc');
                    const bgColor = isCurrent ? (darkMode ? 'rgba(244, 67, 54, 0.1)' : '#ffebee') : 'transparent';
                    const time = findLogTime(stage.type) || (stage.id === 1 ? moment(ronda?.data_hora_criacao).format('HH:mm DD/MM') : null);

                    return (
                        <React.Fragment key={stage.id}>
                            <TimelineNode
                                label={stage.label}
                                icon={stage.icon}
                                borderColor={color}
                                bgColor={bgColor}
                                pulse={isCurrent}
                                tooltipTitle={`${stage.label}: ${time || 'Pendente'}`}
                                time={time}
                                passed={isPassed}
                                current={isCurrent}
                            />
                            {idx < stages.length - 1 && (
                                <Box sx={{ flexGrow: 1, height: 2, bgcolor: isPassed ? '#2e7d32' : (darkMode ? '#333' : '#eee'), mt: 2 }} />
                            )}
                        </React.Fragment>
                    );
                })}
            </Box>
        </Box>
    );
};

export default TimelineRonda;
